import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CURRENT_CLIENT } from '@/constants'
import { loginByCodeLabel } from '@/constants/auth-login-code'
import { confirmLoginCode, requestLoginCode } from '@/lib/login-by-code'
import { useAuth } from '@/hooks/use-auth'
import { useNavigate } from 'react-router-dom'

type Step = 'idle' | 'code_sent' | 'confirming'

interface LoginByCodeFormProps {
  initialEmail?: string
  disabled?: boolean
  onSuccess?: () => void
}

export function LoginByCodeForm({ initialEmail = '', disabled, onSuccess }: LoginByCodeFormProps) {
  const [expanded, setExpanded] = useState(false)
  const [step, setStep] = useState<Step>('idle')
  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { loginWithTokens } = useAuth()
  const navigate = useNavigate()

  const sendCode = async () => {
    setError('')
    setInfo('')

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    setIsLoading(true)
    try {
      const result = await requestLoginCode(email.trim())
      if (!result.success) {
        setError(result.error ?? 'Could not send sign-in code')
        return
      }
      setStep('code_sent')
      setInfo('If an account exists for that email, we sent a sign-in code. Check your inbox.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRequest = async (event: React.FormEvent) => {
    event.preventDefault()
    await sendCode()
  }

  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    setInfo('')

    if (!code.trim()) {
      setError('Enter the code from your email')
      return
    }

    setIsLoading(true)
    setStep('confirming')
    try {
      const result = await confirmLoginCode(code.trim())
      if (result.approval_pending) {
        navigate('/register/pending', { replace: true })
        return
      }
      if (result.approval_rejected) {
        setStep('code_sent')
        setError(result.error ?? 'This account was not approved.')
        return
      }
      if (!result.success || !result.access_token || !result.user) {
        setStep('code_sent')
        setError(result.error ?? 'Invalid or expired code')
        return
      }

      loginWithTokens(result.access_token, result.user)
      onSuccess?.()
      if (result.onboarding_redirect) {
        navigate('/onboarding', { replace: true })
      } else {
        navigate('/profile', { replace: true })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!expanded) {
    return (
      <div className="text-center">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm font-normal text-muted-foreground underline underline-offset-4 hover:text-foreground"
          disabled={disabled}
          onClick={() => setExpanded(true)}
        >
          {loginByCodeLabel()}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 rounded-md border border-border/60 bg-muted/20 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Sign in with email code</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-2 py-1 text-xs"
          disabled={disabled || isLoading}
          onClick={() => {
            setExpanded(false)
            setStep('idle')
            setCode('')
            setError('')
            setInfo('')
          }}
        >
          Cancel
        </Button>
      </div>

      {error ? (
        <div
          className="p-3 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10"
          role="alert"
        >
          {error}
        </div>
      ) : null}
      {info ? (
        <div
          className="p-3 text-sm text-success border border-success/20 rounded-md bg-success/10"
          role="status"
        >
          {info}
        </div>
      ) : null}

      {step === 'idle' ? (
        <form className="space-y-3" onSubmit={handleRequest}>
          <div className="grid gap-2">
            <Label htmlFor="login-code-email">Email</Label>
            <Input
              id="login-code-email"
              type="email"
              autoComplete="email"
              placeholder={CURRENT_CLIENT.placeholderEmail}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              disabled={disabled || isLoading}
            />
          </div>
          <Button type="submit" fullWidth disabled={disabled || isLoading}>
            {isLoading ? 'Sending…' : 'Send code'}
          </Button>
        </form>
      ) : (
        <form className="space-y-3" onSubmit={handleConfirm}>
          <div className="grid gap-2">
            <Label htmlFor="login-code-value">Sign-in code</Label>
            <Input
              id="login-code-value"
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              placeholder="Enter code from email"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
              disabled={disabled || isLoading}
            />
          </div>
          <Button type="submit" fullWidth disabled={disabled || isLoading}>
            {isLoading && step === 'confirming' ? 'Verifying…' : 'Verify code'}
          </Button>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs font-normal text-muted-foreground"
            disabled={disabled || isLoading}
            onClick={() => void sendCode()}
          >
            Resend code
          </Button>
        </form>
      )}
    </div>
  )
}
